import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchMachines, updateMachineStatus, setDefaultMachine } from '../store/slices/machinesSlice';

const MachineStatusBadge = ({ status }) => {
  const colors = {
    idle: 'secondary',
    active: 'success',
    maintenance: 'warning',
    error: 'danger',
    offline: 'dark'
  };

  const labels = {
    idle: 'Inactief',
    active: 'Actief',
    maintenance: 'Onderhoud',
    error: 'Storing',
    offline: 'Offline'
  };

  return (
    <span className={`badge bg-${colors[status] || 'secondary'}`}>
      {labels[status] || status}
    </span>
  );
};

const Machines = () => {
  const dispatch = useDispatch();
  const { machines, loading } = useSelector((state) => state.machines);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');

  useEffect(() => {
    dispatch(fetchMachines());
  }, [dispatch]);

  const handleStatusChange = () => {
    if (selectedMachine && newStatus) {
      dispatch(updateMachineStatus({ id: selectedMachine._id, status: newStatus }));
      setShowStatusModal(false);
      setSelectedMachine(null);
    }
  };

  const handleSetDefault = (machineId) => {
    dispatch(setDefaultMachine(machineId));
  };

  const statuses = [
    { value: 'idle', label: 'Inactief' },
    { value: 'active', label: 'Actief' },
    { value: 'maintenance', label: 'Onderhoud' },
    { value: 'error', label: 'Storing' },
    { value: 'offline', label: 'Offline' }
  ];

  return (
    <div className="machines-page">
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <h1>Machines</h1>
          <p className="text-muted">Overzicht en beheer van productiemachines</p>
        </div>
        <Link to="/machines/new" className="btn btn-primary">
          <i className="bi bi-plus-lg me-2"></i>
          Nieuwe machine
        </Link>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Laden...</span>
          </div>
        </div>
      ) : !machines || machines.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="bi bi-gear fs-1 text-muted d-block mb-3"></i>
            <p className="text-muted">Geen machines geconfigureerd</p>
            <Link to="/machines/new" className="btn btn-primary">
              Machine toevoegen
            </Link>
          </div>
        </div>
      ) : (
        <div className="row">
          {machines.map((machine) => (
            <div key={machine._id} className="col-lg-4 col-md-6 mb-4">
              <div className={`card h-100 ${machine.isDefault ? 'border-primary' : ''}`}>
                <div className="card-header d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <h5 className="mb-0">{machine.name}</h5>
                    {machine.isDefault && (
                      <span className="badge bg-primary ms-2">Standaard</span>
                    )}
                  </div>
                  <MachineStatusBadge status={machine.status} />
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <span className="text-muted">Type:</span>
                    <span className="ms-2">{machine.type || '-'}</span>
                  </div>
                  <div className="mb-3">
                    <span className="text-muted">Locatie:</span>
                    <span className="ms-2">{machine.location || '-'}</span>
                  </div>
                  <div className="mb-3">
                    <span className="text-muted">Huidige belasting:</span>
                    <div className="progress mt-1" style={{ height: '8px' }}>
                      <div
                        className={`progress-bar bg-${
                          machine.currentLoad > 80 ? 'danger' :
                          machine.currentLoad > 50 ? 'warning' : 'success'
                        }`}
                        style={{ width: `${machine.currentLoad || 0}%` }}
                      ></div>
                    </div>
                    <small className="text-muted">{machine.currentLoad || 0}%</small>
                  </div>
                  <div className="mb-3">
                    <span className="text-muted">Materialen:</span>
                    <div className="mt-1">
                      {machine.supportedMaterials?.length > 0 ? (
                        machine.supportedMaterials.map((mat, index) => (
                          <span key={index} className="badge bg-light text-dark me-1 mb-1">
                            {mat}
                          </span>
                        ))
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </div>
                  </div>
                  {machine.currentJob && (
                    <div className="alert alert-info mb-0 py-2">
                      <small>
                        <i className="bi bi-play-circle me-1"></i>
                        Huidige job: {machine.currentJob.name || machine.currentJob.cutListId || 'Onbekend'}
                      </small>
                    </div>
                  )}
                </div>
                <div className="card-footer bg-transparent d-flex gap-2">
                  <Link
                    to={`/machines/${machine._id}`}
                    className="btn btn-outline-primary btn-sm flex-grow-1"
                  >
                    <i className="bi bi-eye me-1"></i>
                    Details
                  </Link>
                  <button
                    className={`btn btn-sm ${machine.isDefault ? 'btn-primary' : 'btn-outline-warning'}`}
                    onClick={() => handleSetDefault(machine._id)}
                    title={machine.isDefault ? 'Dit is de standaard machine' : 'Instellen als standaard'}
                    disabled={machine.isDefault}
                  >
                    <i className={`bi ${machine.isDefault ? 'bi-star-fill' : 'bi-star'}`}></i>
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => {
                      setSelectedMachine(machine);
                      setNewStatus(machine.status);
                      setShowStatusModal(true);
                    }}
                  >
                    <i className="bi bi-pencil"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Status Modal */}
      {showStatusModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Status wijzigen - {selectedMachine?.name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowStatusModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Nieuwe status</label>
                  <select
                    className="form-select"
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                  >
                    {statuses.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowStatusModal(false)}
                >
                  Annuleren
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleStatusChange}
                >
                  Opslaan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Machines;
